import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CandidateLayout } from "@/components/candidate/CandidateLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { guides } from "@/data/guidesData";
import { Download, FileText, Loader2, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNoIndex } from "@/hooks/useCanonicalTag";

export default function GuidesExport() {
  useNoIndex();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generatingPDF, setGeneratingPDF] = useState<string | null>(null);

  useEffect(() => {
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/login"); return; }

      const { data: profile } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', user.id)
        .single();

      if (!profile?.is_admin) { navigate("/candidate/dashboard"); return; }
      setIsAdmin(true);
    } catch (error) {
      console.error("Error checking admin status:", error);
      navigate("/candidate/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async (slug: string) => {
    setGeneratingPDF(slug);
    try {
      const guide = guides.find(g => g.slug === slug);
      if (!guide) throw new Error("Guia não encontrado");

      // Dynamic imports to keep bundle small
      const [{ pdf }, { GuidePDFDocument }, { guideDataMap }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('@/components/admin/GuidePDFDocument'),
        import('@/components/guides/content/pdf-exports'),
      ]);

      const data = guideDataMap[slug];
      if (!data) throw new Error("Dados do guia não encontrados");

      const blob = await pdf(<GuidePDFDocument guide={guide} data={data} />).toBlob();
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `guia-${slug}-datahireclub.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "PDF gerado com sucesso!",
        description: `O arquivo guia-${slug}-datahireclub.pdf foi baixado.`,
      });
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      toast({
        title: "Erro ao gerar PDF",
        description: "Tente novamente em alguns segundos.",
        variant: "destructive",
      });
    } finally {
      setGeneratingPDF(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) return null;

  const allowedSlugs = ['engenheiro-ia-2026', 'transicao-carreira-dados', 'salarios-dados-2026'];
  const exportableGuides = guides.filter(g => allowedSlugs.includes(g.slug));

  return (
    <CandidateLayout maxWidth="6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Exportar Guias</h1>
        <p className="text-muted-foreground">
          Gere PDFs dos guias para usar como iscas digitais. Os arquivos são otimizados para impressão.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {exportableGuides.map((guide) => (
          <Card key={guide.slug} className="hover:shadow-md transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${guide.categoryColor}`}>
                  {guide.category}
                </span>
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {guide.readTime}
                </span>
              </div>
              <h3 className="font-semibold text-sm mb-2 line-clamp-2">{guide.title}</h3>
              <p className="text-xs text-muted-foreground mb-4 line-clamp-2">{guide.description}</p>
              <Button
                size="sm"
                className="w-full"
                onClick={() => handleGeneratePDF(guide.slug)}
                disabled={generatingPDF !== null}
              >
                {generatingPDF === guide.slug ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Gerando...</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" />Gerar PDF</>
                )}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mt-8 border-primary/20 bg-primary/5">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <FileText className="w-8 h-8 text-primary flex-shrink-0" />
            <div>
              <h3 className="font-semibold mb-2">Como usar os PDFs</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Use como isca digital em landing pages de captação</li>
                <li>• Configure envio automático por email após cadastro</li>
                <li>• Distribua em comunidades e redes sociais</li>
                <li>• Os PDFs incluem logo e URL do site no rodapé</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </CandidateLayout>
  );
}
